import { useState, useMemo } from "react";
import { GraduationCap, Phone, Mail, User, Globe2, Loader2, CheckCircle2 } from "lucide-react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { SearchableCombobox } from "@/components/SearchableCombobox";
import { COUNTRIES, COUNTRY_BY_NAME } from "@/lib/countries";
import { toast } from "sonner";
import { api, formatApiErrorDetail } from "@/lib/apiClient";

// Phone number: very light client-side validation. Once a dial code is picked
// the local-number portion needs to be 5–15 digits (E.164 max 15 incl. cc).
const isValidLocalNumber = (v) => /^[0-9]{5,15}$/.test(v.replace(/[ \-]/g, ""));

// Items for the country dropdown — searchable by name, ISO and dial code.
const COUNTRY_ITEMS = COUNTRIES.map(c => ({
  value: c.name,
  label: c.name,
  leading: <span className="text-base" aria-hidden>{c.flag}</span>,
  trailing: c.dial,
  searchText: `${c.name} ${c.iso} ${c.dial} ${c.flag}`,
}));

// Items for the dial-code dropdown — same data, different presentation:
// dial code is the primary value, country name shown as helper.
const DIAL_ITEMS = COUNTRIES.map(c => ({
  // Use `dial|iso` as the unique value so two countries sharing +1 (US/CA)
  // remain individually selectable.
  value: `${c.dial}|${c.iso}`,
  label: c.dial,
  leading: <span className="text-base" aria-hidden>{c.flag}</span>,
  trailing: c.name,
  searchText: `${c.dial} ${c.name} ${c.iso} ${c.flag}`,
}));

// Resolve a default dial-code entry from a country name (used when the user
// has already picked a country on a region-specific page).
const dialValueFor = (countryName) => {
  const c = COUNTRY_BY_NAME[countryName];
  return c ? `${c.dial}|${c.iso}` : "";
};

export const LeadFormDialog = ({ open, onOpenChange, defaultCountry = "", source = "live-classes" }) => {
  const [name, setName] = useState("");
  const [country, setCountry] = useState(defaultCountry);
  const [dial, setDial] = useState(() => dialValueFor(defaultCountry));
  const [email, setEmail] = useState("");
  const [localNumber, setLocalNumber] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState(false);

  // Reset transient state every time the dialog closes/opens (handled here
  // rather than in an effect to avoid the React 19 "set-state-in-effect" lint).
  const handleOpenChange = (v) => {
    if (!v) setDone(false);
    if (v && defaultCountry && !country) {
      setCountry(defaultCountry);
      setDial(dialValueFor(defaultCountry));
    }
    onOpenChange(v);
  };

  // When the user changes the country, auto-sync the dial code (only if they
  // haven't manually picked a different dial code yet, so we don't clobber a
  // deliberate choice e.g. UK national living in Saudi Arabia).
  const handleCountryChange = (newCountry) => {
    setCountry(newCountry);
    const expected = dialValueFor(newCountry);
    if (expected) setDial(expected);
  };

  const dialCode = useMemo(() => (dial ? dial.split("|")[0] : ""), [dial]);

  const submit = async (e) => {
    e.preventDefault();
    if (!name.trim()) { toast.error("Please enter your name"); return; }
    if (!email.includes("@")) { toast.error("Please enter a valid email"); return; }
    if (!country) { toast.error("Please select your country"); return; }
    if (!dialCode) { toast.error("Please pick your country dial code"); return; }
    if (!isValidLocalNumber(localNumber)) { toast.error("Please enter a valid mobile number (digits only)"); return; }

    setBusy(true);
    try {
      await api.post("/lessons/waitlist", {
        email: email.trim(),
        name: name.trim(),
        country,
        mobile: `${dialCode} ${localNumber.replace(/[ \-]/g, "")}`,
        note: `lead-form:${source}`,
      });
      setDone(true);
      toast.success("Jazākallāh khayr — we'll contact you within 12 hours.");
    } catch (e2) {
      toast.error(formatApiErrorDetail(e2.response?.data?.detail) || "Couldn't submit — please try again.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleOpenChange}>
      <DialogContent
        className="max-w-md sm:max-w-lg p-0 overflow-hidden border-accent/40"
        data-testid="lead-form-dialog"
      >
        {/* Hero strip */}
        <div className="bg-gradient-to-br from-emerald-700 via-emerald-600 to-emerald-800 text-white px-6 sm:px-7 py-5">
          <div className="flex items-center gap-2.5">
            <span className="w-9 h-9 rounded-full bg-amber-300/95 text-emerald-900 inline-flex items-center justify-center">
              <GraduationCap className="w-5 h-5" />
            </span>
            <DialogHeader className="p-0 text-left">
              <DialogTitle className="font-display text-xl text-white">Book a Free Quran Trial Class</DialogTitle>
              <DialogDescription className="text-amber-100/90 text-xs">
                1-to-1 live with a verified teacher · No card · No commitment
              </DialogDescription>
            </DialogHeader>
          </div>
        </div>

        {done ? (
          <div className="px-6 sm:px-7 py-8 text-center" data-testid="lead-form-success">
            <CheckCircle2 className="w-12 h-12 text-primary mx-auto" />
            <p className="font-display text-xl mt-3">You&apos;re booked — Jazākallāh khayr</p>
            <p className="text-sm text-foreground/70 mt-2">
              Our team will contact you on <strong>{dialCode} {localNumber}</strong> or <strong>{email}</strong> within
              12 hours to confirm your free trial slot.
            </p>
            <Button
              onClick={() => { setDone(false); onOpenChange(false); }}
              className="mt-5 pill-btn bg-primary hover:bg-primary/90"
              data-testid="lead-form-close-btn"
            >
              Close
            </Button>
          </div>
        ) : (
          <form onSubmit={submit} className="px-6 sm:px-7 py-5 space-y-3.5" data-testid="lead-form">
            <div className="space-y-1.5">
              <Label htmlFor="lead-name" className="text-xs uppercase tracking-wide text-foreground/65">
                <User className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Full Name
              </Label>
              <Input
                id="lead-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="e.g. Ahmad Khan"
                className="h-11 rounded-xl"
                required
                data-testid="lead-form-name"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-foreground/65">
                <Globe2 className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Country
              </Label>
              <SearchableCombobox
                items={COUNTRY_ITEMS}
                value={country}
                onValueChange={handleCountryChange}
                placeholder="Select your country…"
                searchPlaceholder="Type to search country, code or flag…"
                emptyText="No country matches that search."
                triggerTestId="lead-form-country"
                popoverTestId="lead-form-country-popover"
                itemTestIdPrefix="country-option"
                buttonId="lead-country"
              />
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="lead-email" className="text-xs uppercase tracking-wide text-foreground/65">
                <Mail className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Email Address
              </Label>
              <Input
                id="lead-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@example.com"
                className="h-11 rounded-xl"
                required
                data-testid="lead-form-email"
              />
            </div>

            <div className="space-y-1.5">
              <Label className="text-xs uppercase tracking-wide text-foreground/65">
                <Phone className="w-3.5 h-3.5 inline mr-1 -mt-0.5" /> Mobile Number
              </Label>
              <div className="grid grid-cols-[40%_60%] gap-2">
                <SearchableCombobox
                  items={DIAL_ITEMS}
                  value={dial}
                  onValueChange={setDial}
                  placeholder="Code"
                  searchPlaceholder="Search +44, UK, 🇬🇧…"
                  emptyText="No dial code matches."
                  triggerTestId="lead-form-dial-code"
                  popoverTestId="lead-form-dial-popover"
                  itemTestIdPrefix="dial-option"
                />
                <Input
                  type="tel"
                  inputMode="numeric"
                  value={localNumber}
                  onChange={(e) => setLocalNumber(e.target.value)}
                  placeholder="7400 123456"
                  className="h-11 rounded-xl"
                  required
                  data-testid="lead-form-mobile"
                  aria-label="Mobile number"
                />
              </div>
              <p className="text-[11px] text-foreground/55">We&apos;ll WhatsApp you to confirm your free trial slot.</p>
            </div>

            <Button
              type="submit"
              disabled={busy}
              className="pill-btn w-full h-12 bg-primary hover:bg-primary/90 text-base"
              data-testid="lead-form-submit"
            >
              {busy ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Submitting…</>) : "Book My Free Trial Class"}
            </Button>

            <p className="text-[11px] text-center text-foreground/55 pt-1">
              By submitting you agree to be contacted about your free trial class. We never share your details.
            </p>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
};
